import {Injectable} from "@angular/core";
import {FormService} from "../../../umbrella-common/services/form.service";
import {FormBuilder, FormControl, FormGroup, Validators} from "@angular/forms";
import {AttachedQuestion, AttachedQuestionQuestionType, EstimateQuestion, EstimateQuestionType, Order, Product, StandardName} from "../../../store/state/order.state";
import {AppState} from "../../../store/state/app.state";
import {Store} from "@ngrx/store";
import {BaseOrderFormService} from "../common/base-order-form.service";
import {distinctUntilChanged, takeWhile} from "rxjs/operators";
import {OrderActions} from "../../../store/actions/order.actions";
import {prepareIso27701Questions} from "../../resolvers/estimate-questions.resolver";
import {UmbrellaValidators} from "../../../umbrella-common/validation/umbrella-validators";
import {TranslateService} from "@ngx-translate/core";

@Injectable()
export class EstimateQuestionsFormService extends FormService implements BaseOrderFormService {

	private allEstimateQuestions: Map<StandardName, EstimateQuestion[]> = new Map<StandardName, EstimateQuestion[]>();
	private allControlNamesInOrder: string[];

	constructor(private fb: FormBuilder, private store: Store<AppState>, translate: TranslateService) {
		super(translate);
	}

	protected buildFormFor(formKey: string, [estimateQuestions, product]: [EstimateQuestion[], Product]) {
		this.allEstimateQuestions.set(product.standard.name, estimateQuestions);
		this.allControlNamesInOrder = this.getAllControlNamesInOrder(estimateQuestions, formKey);

		const form = this.fb.group({});
		this.buildLongOptionSingleChoiceControls(estimateQuestions, product, formKey, form);
		return form;
	}

	private buildLongOptionSingleChoiceControls(estimateQuestions: EstimateQuestion[], product: Product, standardName, form) {
		estimateQuestions.filter(eq => eq.type == EstimateQuestionType.LONG_OPTION_SINGLE_CHOICE).forEach(eq => {
			const control = this.fb.control(this.getAnswer(eq.questionId, product), UmbrellaValidators.radiobuttonRequired());
			const controlName = this.getEstimateQuestionFormControlName(eq.questionId, standardName);
			form.addControl(controlName, control);

			eq.attachedQuestions.forEach(aq => {
				if (this.shouldAddAttachedQuestion(aq, eq)) {
					const answer = this.getAttachedQuestionAnswer(eq.questionId, aq.type, product);
					this.addAttachedQuestionFormControl(answer, form, eq, aq, standardName);
				}
			});

			control.valueChanges.pipe(takeWhile(() => this.isSubFormInitialized(standardName)), distinctUntilChanged())
				.subscribe((answer: string) => {
					this.addOrRemoveAttachedQuestionControlIfNeeded(standardName, eq, answer, form);
					this.store.dispatch(OrderActions.ESTIMATE_QUESTION_ANSWERED({
						standardName,
						question: eq,
						answer: [answer]
					}));
				});
		});
	}

	private shouldAddAttachedQuestion(attachedQuestion: AttachedQuestion, estimateQuestion: EstimateQuestion) {
		return attachedQuestion.parentQuestionAnswerValue.includes(this.getEstimateQuestionAnswerPosition(estimateQuestion.chosenOptionIds[0], estimateQuestion.optionIds));
	}

	private addAttachedQuestionFormControl(controlValue: string, form, estimateQuestion: EstimateQuestion, attachedQuestion: AttachedQuestion, standardName) {
		const aqControl = attachedQuestion.questionType == AttachedQuestionQuestionType.TEXT ? this.fb.control(controlValue, [Validators.required, UmbrellaValidators.textLengthBetween(3, 5000)]) :
			this.fb.control(controlValue, UmbrellaValidators.radiobuttonRequired());
		form.addControl(this.getEstimateAttachedQuestionFormControlName(estimateQuestion.questionId, standardName, attachedQuestion.type), aqControl);
		this.initAttachedQuestionFormControl(standardName, attachedQuestion, estimateQuestion, aqControl);
	}

	private initAttachedQuestionFormControl(standardName, attachedQuestion: AttachedQuestion, estimateQuestion: EstimateQuestion, aqControl: FormControl) {
		aqControl.valueChanges.pipe(takeWhile(() => this.isSubFormInitialized(standardName)), distinctUntilChanged())
			.subscribe(answer => {
				this.store.dispatch(OrderActions.ESTIMATE_QUESTION_ATTACHED_QUESTION_ANSWERED({
					standardName: standardName,
					estimateQuestionId: estimateQuestion.questionId,
					attachedQuestion: attachedQuestion,
					answer
				}));
			});
	}

	private addOrRemoveAttachedQuestionControlIfNeeded(standardName, estimateQuestion: EstimateQuestion, answer: string, form: FormGroup) {
		estimateQuestion.attachedQuestions.forEach(aq => {
			if (aq.parentQuestionAnswerValue.includes(this.getEstimateQuestionAnswerPosition(answer, estimateQuestion.optionIds))) {
				if (!this.containsAttachedQuestionFormControl(standardName, estimateQuestion, form, aq.type)) {
					this.addAttachedQuestionFormControl(aq.answer, form, estimateQuestion, aq, standardName);
				}
			} else {
				if (this.containsAttachedQuestionFormControl(standardName, estimateQuestion, form, aq.type)) {
					form.removeControl(this.getEstimateAttachedQuestionFormControlName(estimateQuestion.questionId, standardName, aq.type));
				}
			}
		});
	}

	private containsAttachedQuestionFormControl(standardName, estimateQuestion: EstimateQuestion, form: FormGroup, attachedQuestionType: string): boolean {
		return form.contains(this.getEstimateAttachedQuestionFormControlName(estimateQuestion.questionId, standardName, attachedQuestionType));
	}

	private getEstimateQuestionAnswerPosition(chosenOptionId: string, optionIds: string[]): number {
		for (let i = 0; i < optionIds.length; i++) {
			if (optionIds[i] == chosenOptionId) {
				return i + 1;
			}
		}
		return -1;
	}

	private getAnswer(questionId: string, product: Product): string | null {
		const question = product.estimateQuestions && product.estimateQuestions.find(pf => pf.questionId === questionId);
		return !!question && question.chosenOptionIds && question.chosenOptionIds.length > 0 ? question.chosenOptionIds[0] : null;
	}

	private getAttachedQuestionAnswer(questionId: string, type: string, product: Product): string | null {
		const question = product.estimateQuestions && product.estimateQuestions.find(pf => pf.questionId === questionId);
		const attachedQuestion = !!question ? question.attachedQuestions.find(aq => aq.type === type) : null;
		return !!attachedQuestion ? attachedQuestion.answer : null;
	}

	public getEstimateQuestionFormControlName(questionId: string, formKey: string) {
		return questionId + formKey;
	}

	public getEstimateAttachedQuestionFormControlName(questionId: string, formKey: string, attachedQuestionType: string) {
		return questionId + '-' + attachedQuestionType + '-' + formKey;
	}

	public answerSingleChoiceQuestion(questionId: string, standardName: StandardName, answer: string) {
		this.updateFormControl(this.getEstimateQuestionFormControlName(questionId, standardName), answer, standardName);
	}

	getControlNamesInOrder(): string[] {
		return this.allControlNamesInOrder;
	}

	fillOrder(order: Order): Order {
		this.getAllFormKeys().filter(formKey => this.isSubFormInitialized(formKey)).forEach(formKey => {
			const product = order.products.find(p => p.standard.name === formKey);

			product.estimateQuestions = this.allEstimateQuestions.get(product.standard.name).map(eq => {
				this.fillQuestionAnswer(eq, formKey, this.getEstimateQuestionFormControlName(eq.questionId, StandardName[formKey]));

				eq.attachedQuestions.forEach(aq => {
					this.fillAttachedQuestionAnswer(aq, formKey, this.getEstimateAttachedQuestionFormControlName(eq.questionId, product.standard.name, aq.type), eq);
				});

				return eq;
			});

			if (product.standard.name == StandardName.ISO_27701) {
				product.estimateQuestions = prepareIso27701Questions(product.estimateQuestions, order.products.find(p => p.standard.name == StandardName.ISO_27001).estimateQuestions);
			}
		});
		return order;
	}

	private fillQuestionAnswer(question: EstimateQuestion, formKey: string, controlName: string) {
		if (question.type == EstimateQuestionType.LONG_OPTION_SINGLE_CHOICE) {
			question.chosenOptionIds = [this.getValueFor(controlName, formKey)];
		}
	}

	private fillAttachedQuestionAnswer(attachedQuestion: AttachedQuestion, formKey: string, controlName: string, estimateQuestion: EstimateQuestion) {
		attachedQuestion.parentQuestionAnswerValue.includes(this.getEstimateQuestionAnswerPosition(estimateQuestion.chosenOptionIds[0], estimateQuestion.optionIds)) ?
			attachedQuestion.answer = this.getValueFor(controlName, formKey) : attachedQuestion.answer = null;
	}

	private getAllControlNamesInOrder(estimateQuestions: EstimateQuestion[], formKey: string) {
		const controlsFromPreviousProducts = this.allControlNamesInOrder != null ? this.allControlNamesInOrder : [];
		const controlsFromCurrentProduct = estimateQuestions.flatMap(eq => [this.getEstimateQuestionFormControlName(eq.questionId, formKey)].concat(this.getControlNamesForAttachedQuestions(eq, formKey)));
		return controlsFromPreviousProducts.concat(controlsFromCurrentProduct);
	}

	private getControlNamesForAttachedQuestions(question: EstimateQuestion, formKey: string): string[] {
		return question.attachedQuestions.map(aq => this.getEstimateAttachedQuestionFormControlName(question.questionId, formKey, aq.type))
	}
}
